import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { dictionary, localizePath } from "@/i18n/translations";
import { api, buildAuthHeaders } from "@/lib/api";
import { setDocumentMeta } from "@/lib/seo";

export default function DashboardOverviewPage({ lang }) {
  const copy = dictionary[lang];
  const { session, user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    visible: 0,
    sale: 0,
    rent: 0,
    pendingUsers: 0,
  });

  useEffect(() => {
    setDocumentMeta({
      lang,
      title: `${copy.dashboardNav.dashboard} | ${copy.nav.brand}`,
      description: copy.dashboard.subtitle,
    });
  }, [copy.dashboard.subtitle, copy.dashboardNav.dashboard, copy.nav.brand, lang]);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const headers = { headers: buildAuthHeaders(session) };
        const propertiesResponse = await api.get("/admin/properties", headers);
        const properties = propertiesResponse.data || [];

        const nextStats = {
          total: properties.length,
          visible: properties.filter((item) => item.is_visible).length,
          sale: properties.filter((item) => item.listing_type === "sale").length,
          rent: properties.filter((item) => item.listing_type === "rent").length,
          pendingUsers: 0,
        };

        if (user?.role === "admin") {
          const pendingResponse = await api.get("/admin/users/pending", headers);
          nextStats.pendingUsers = (pendingResponse.data || []).length;
        }

        setStats(nextStats);
      } catch {
        toast.error(copy.common.requestFailed);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [copy.common.requestFailed, session, user?.role]);

  const cards = useMemo(
    () => [
      { key: "total", label: copy.dashboardOverview.totalProperties, value: stats.total },
      { key: "visible", label: copy.dashboardOverview.visibleProperties, value: stats.visible },
      { key: "sale", label: copy.dashboardOverview.saleProperties, value: stats.sale },
      { key: "rent", label: copy.dashboardOverview.rentProperties, value: stats.rent },
      ...(user?.role === "admin"
        ? [{ key: "pending", label: copy.dashboardOverview.pendingRequests, value: stats.pendingUsers }]
        : []),
    ],
    [copy.dashboardOverview, stats.pendingUsers, stats.rent, stats.sale, stats.total, stats.visible, user?.role],
  );

  return (
    <div className="space-y-6" data-testid="dashboard-overview-page">
      <section className="space-y-2" data-testid="dashboard-overview-header">
        <h1 className="text-3xl font-semibold text-stone-900 sm:text-4xl" data-testid="dashboard-overview-title">
          {copy.dashboardNav.dashboard}
        </h1>
        <p className="text-sm text-stone-600" data-testid="dashboard-overview-subtitle">
          {copy.dashboardOverview.subtitle}
        </p>
      </section>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="dashboard-overview-stats-grid">
        {cards.map((card) => (
          <Card key={card.key} className="border-gold-500/20 bg-white/90" data-testid={`dashboard-overview-card-${card.key}`}>
            <CardHeader>
              <CardTitle className="text-sm text-stone-600" data-testid={`dashboard-overview-card-label-${card.key}`}>
                {card.label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-semibold text-stone-900" data-testid={`dashboard-overview-card-value-${card.key}`}>
                {loading ? "..." : card.value}
              </p>
            </CardContent>
          </Card>
        ))}
      </section>

      <Card className="border-gold-500/20 bg-white/90" data-testid="dashboard-overview-quick-actions-card">
        <CardHeader>
          <CardTitle data-testid="dashboard-overview-quick-actions-title">{copy.dashboardOverview.quickActions}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3" data-testid="dashboard-overview-quick-actions-buttons">
          <Link to={localizePath(lang, "/dashboard/properties")} data-testid="dashboard-overview-go-properties-link">
            <Button data-testid="dashboard-overview-go-properties-button">{copy.dashboardNav.properties}</Button>
          </Link>
          <Link to={localizePath(lang, "/dashboard/properties/new")} data-testid="dashboard-overview-go-add-property-link">
            <Button variant="outline" data-testid="dashboard-overview-go-add-property-button">{copy.dashboardNav.addProperty}</Button>
          </Link>
          <Link to={localizePath(lang, "/dashboard/settings")} data-testid="dashboard-overview-go-settings-link">
            <Button variant="outline" data-testid="dashboard-overview-go-settings-button">{copy.dashboardNav.settings}</Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
